import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import ImageUpload from './ImageUpload';

const EMPTY_CLIENT = {
  slug: '', name: '', headshot: '', bio: '',
  playing_age: '', height: '', location: '',
  hair_colour: '', eye_colour: '',
  accents: [], skills: [], training: [], credits: [],
  spotlight_url: '', showreel_url: '', featured: false, gallery: [],
};

/**
 * Full client editor — supports create + edit. Handles the deep-structured
 * fields (accents, skills as comma-separated inputs; credits + training as
 * repeatable row arrays).
 */
export default function ClientEditor({ initial, onCancel, onSave }) {
  const [form, setForm] = useState({ ...EMPTY_CLIENT, ...initial });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isEdit = !!initial?.id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.slug.trim()) return toast.error('Slug is required (used in the URL).');
    if (!form.name.trim()) return toast.error('Name is required.');
    // Clean out empty rows
    const cleaned = {
      ...form,
      accents: (form.accents || []).filter((a) => a && a.trim()),
      skills: (form.skills || []).filter((s) => s && s.trim()),
      credits: (form.credits || []).filter((c) => c && (c.production || c.role || c.medium || c.company)),
      training: (form.training || []).filter((t) => t && (t.institution || t.course || t.year)),
      gallery: (form.gallery || []).filter((g) => g && g.trim()),
    };
    onSave(cleaned);
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-black/8 p-8 md:p-10 max-w-[1000px]">
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="eyebrow text-[#C5A059]">Editor</p>
          <h2 className="font-heading uppercase text-2xl md:text-3xl text-[#002b5c] mt-2 leading-tight">
            {isEdit ? `Edit — ${form.name || 'Client'}` : 'New client'}
          </h2>
        </div>
        <button type="button" onClick={onCancel} className="p-2 rounded-full text-[#6b6b6b] hover:text-[#002b5c]">
          <X size={20} />
        </button>
      </div>

      {/* Identity */}
      <Section title="Identity">
        <TextField label="Name" value={form.name} onChange={(v) => upd('name', v)} required />
        <TextField
          label="URL slug"
          value={form.slug}
          onChange={(v) => upd('slug', v)}
          required
          hint="Used as /clients/{slug} — lowercase, hyphenated. Do NOT change on an existing client (breaks Google indexing)."
        />
      </Section>

      {/* Headshot */}
      <Section title="Headshot" oneColumn>
        <ImageUpload
          label="Headshot photo"
          value={form.headshot}
          onChange={(v) => upd('headshot', v)}
          hint="Portrait orientation looks best. If you paste a URL, use https://"
        />
      </Section>

      {/* Casting stats */}
      <Section title="Casting details">
        <TextField label="Playing age" value={form.playing_age} onChange={(v) => upd('playing_age', v)} placeholder="e.g. 25-35" />
        <TextField label="Height" value={form.height} onChange={(v) => upd('height', v)} placeholder='e.g. 5&#39;10" (178cm)' />
        <TextField label="Location" value={form.location} onChange={(v) => upd('location', v)} placeholder="e.g. London, United Kingdom" />
        <TextField label="Hair colour" value={form.hair_colour} onChange={(v) => upd('hair_colour', v)} />
        <TextField label="Eye colour" value={form.eye_colour} onChange={(v) => upd('eye_colour', v)} />
      </Section>

      {/* Bio */}
      <Section title="Bio" oneColumn>
        <div>
          <Label>Bio</Label>
          <textarea
            value={form.bio}
            onChange={(e) => upd('bio', e.target.value)}
            rows={5}
            className="mt-2 w-full bg-[#faf8f4] rounded-lg border border-black/10 focus:border-[#002b5c] py-3 px-4 outline-none text-sm leading-relaxed"
          />
        </div>
      </Section>

      {/* External links */}
      <Section title="External links">
        <TextField label="Spotlight URL" value={form.spotlight_url} onChange={(v) => upd('spotlight_url', v)} placeholder="https://app.spotlight.com/…" />
        <TextField label="Showreel (YouTube) URL" value={form.showreel_url} onChange={(v) => upd('showreel_url', v)} placeholder="https://www.youtube.com/watch?v=…" />
      </Section>

      {/* Comma-separated lists */}
      <Section title="Skills, accents (comma-separated)">
        <TextField
          label="Accents"
          value={(form.accents || []).join(', ')}
          onChange={(v) => upd('accents', v.split(',').map((x) => x.trim()))}
          placeholder="e.g. RP, Northern English, Scottish"
        />
        <TextField
          label="Skills"
          value={(form.skills || []).join(', ')}
          onChange={(v) => upd('skills', v.split(',').map((x) => x.trim()))}
          placeholder="e.g. Piano, Horse riding, Stage combat"
        />
      </Section>

      {/* Repeatable rows: credits + training */}
      <Section title="Credits" oneColumn>
        <RepeatingRows
          rows={form.credits || []}
          onChange={(rows) => upd('credits', rows)}
          columns={[
            { key: 'medium', label: 'Medium', placeholder: 'Film / TV / Theatre' },
            { key: 'production', label: 'Production', placeholder: 'Show / project name' },
            { key: 'role', label: 'Role', placeholder: 'Character' },
            { key: 'company', label: 'Company / Director', placeholder: 'Producer / director' },
          ]}
          addLabel="Add credit"
        />
      </Section>

      <Section title="Training" oneColumn>
        <RepeatingRows
          rows={form.training || []}
          onChange={(rows) => upd('training', rows)}
          columns={[
            { key: 'institution', label: 'Institution', placeholder: 'Drama school / conservatoire' },
            { key: 'course', label: 'Course', placeholder: 'e.g. BA Acting' },
            { key: 'year', label: 'Year(s)', placeholder: '2018-2021' },
          ]}
          addLabel="Add training"
        />
      </Section>

      <div className="mt-10 flex items-center gap-3">
        <button type="submit" data-testid="client-editor-save" className="inline-flex items-center gap-2 rounded-full bg-[#002b5c] text-white px-8 py-3.5 text-[11px] tracking-[0.24em] uppercase hover:bg-[#001a38] transition-colors">
          <Save size={14} /> Save
        </button>
        <button type="button" onClick={onCancel} className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b] hover:text-[#002b5c] px-4 py-3.5">
          Cancel
        </button>
      </div>
    </form>
  );
}

/* -------- small helpers -------- */
function Section({ title, oneColumn, children }) {
  return (
    <fieldset className="mt-8 border-t border-black/8 pt-6">
      <legend className="text-[11px] tracking-[0.22em] uppercase text-[#C5A059] mb-4">{title}</legend>
      <div className={oneColumn ? 'space-y-4' : 'grid md:grid-cols-2 gap-4'}>
        {children}
      </div>
    </fieldset>
  );
}
function Label({ children }) {
  return <label className="text-[11px] tracking-[0.22em] uppercase text-[#6b6b6b]">{children}</label>;
}
function TextField({ label, value, onChange, required, placeholder, hint }) {
  return (
    <div>
      <Label>{label}{required && <span className="text-[#C5A059] ml-1">*</span>}</Label>
      <input
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        className="mt-2 w-full bg-[#faf8f4] rounded-lg border border-black/10 focus:border-[#002b5c] py-2.5 px-3.5 outline-none text-sm"
      />
      {hint && <p className="mt-1 text-[11px] text-[#6b6b6b]">{hint}</p>}
    </div>
  );
}

function RepeatingRows({ rows, onChange, columns, addLabel }) {
  const setCell = (i, k, v) => {
    const next = rows.slice();
    next[i] = { ...(next[i] || {}), [k]: v };
    onChange(next);
  };
  const removeRow = (i) => {
    const next = rows.slice();
    next.splice(i, 1);
    onChange(next);
  };
  const addRow = () => onChange([...(rows || []), {}]);
  return (
    <div className="space-y-2">
      {rows.map((row, i) => (
        <div key={i} className="grid gap-2 items-center bg-[#faf8f4] rounded-lg p-3 border border-black/8" style={{ gridTemplateColumns: `repeat(${columns.length}, 1fr) 40px` }}>
          {columns.map((c) => (
            <input
              key={c.key}
              type="text"
              value={row[c.key] || ''}
              onChange={(e) => setCell(i, c.key, e.target.value)}
              placeholder={c.placeholder}
              className="bg-white rounded border border-black/10 focus:border-[#002b5c] py-2 px-3 outline-none text-xs"
            />
          ))}
          <button type="button" onClick={() => removeRow(i)} className="text-[#6b6b6b] hover:text-red-500 flex items-center justify-center" aria-label="Remove row">
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button type="button" onClick={addRow} className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#002b5c] px-3 py-2 rounded-full border border-black/15 hover:border-[#002b5c] transition-colors">
        <Plus size={12} /> {addLabel}
      </button>
    </div>
  );
}
